import { useEffect, useState } from "react";
import axios from "axios";
import { Instagram, ArrowUpRight } from "lucide-react";
import { LINKS } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InstagramFeed = () => {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/instagram/featured`)
      .then((r) => {
        if (!cancelled) setPosts(r.data || []);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  // Hide entire section if admin hasn't curated any posts yet — keeps page tidy.
  if (loaded && posts.length === 0) return null;

  return (
    <section
      id="instagram"
      data-testid="instagram-section"
      className="py-20 md:py-28 bg-cream border-t border-charcoal/10"
    >
      <div className="container-edit">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10 md:mb-12">
          <div>
            <div className="overline mb-3">— From the Feed</div>
            <h2 className="font-serif-heading text-3xl md:text-5xl text-charcoal leading-[1.05]" data-testid="ig-title">
              Latest from <span className="italic text-bronze-deep">@primogroupottawa</span>
            </h2>
          </div>
          <a
            href={LINKS.primoInstagram}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-charcoal border-b border-charcoal pb-1 hover:text-bronze hover:border-bronze transition-colors"
            data-testid="ig-follow-link"
          >
            Follow on Instagram <ArrowUpRight size={14} strokeWidth={1.5} />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="ig-grid">
          {posts.slice(0, 3).map((p) => (
            <a
              key={p.id}
              href={p.post_url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block bg-ivory border border-charcoal/10 hover:border-bronze transition-colors"
              data-testid={`ig-post-${p.id}`}
            >
              <div className="relative aspect-square overflow-hidden bg-charcoal/5">
                <img
                  src={p.image_url}
                  alt={p.caption || "Instagram post"}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
                <div className="absolute top-3 right-3 bg-ivory/90 backdrop-blur w-9 h-9 flex items-center justify-center text-charcoal">
                  <Instagram size={14} strokeWidth={1.6} />
                </div>
              </div>
              {p.caption ? (
                <div className="p-5">
                  <p className="text-sm text-ink leading-relaxed line-clamp-3">{p.caption}</p>
                  <div className="overline mt-3 flex items-center gap-1.5 group-hover:text-bronze transition-colors">
                    View Post <ArrowUpRight size={11} strokeWidth={1.5} />
                  </div>
                </div>
              ) : (
                <div className="p-5">
                  <div className="overline flex items-center gap-1.5 group-hover:text-bronze transition-colors">
                    View Post <ArrowUpRight size={11} strokeWidth={1.5} />
                  </div>
                </div>
              )}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InstagramFeed;
